import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

import { API_BASE_URL, API_KEY } from 'constant/apiConstant';

export const companyProductApi = createApi({
  reducerPath: 'companyProductApi',
  tagTypes: ['Post'],
  keepUnusedDataFor: 1,
  baseQuery: fetchBaseQuery({ baseUrl: API_BASE_URL }),
  endpoints: (builder) => ({
    getAuthrizedCompanyDetails: builder.query({
      query: (payload) => ({
        url: `/company-product/list?businessType=${payload.businessType}&companyId=${payload.companyId}&limit=${payload.limit}&offset=${payload.offset}`,
        method: 'GET',
        headers: {
          'Content-type': 'application/json; charset=UTF-8',
          'X-API-KEY': API_KEY,
          Authorization: `Bearer ${payload.token}`
        }
      })
    }),
    getAgencyProducts: builder.query({
      query: (payload) => ({
        url: `/company-product/get-agency-products?agencyId=${payload.agencyId}&limit=${payload.limit}&offset=${payload.offset}&searchBy=${payload.searchBy}&status=${payload.status}`,
        method: 'GET',
        headers: {
          'Content-type': 'application/json; charset=UTF-8',
          'X-API-KEY': API_KEY,
          Authorization: `Bearer ${payload.token}`
        }
      })
    }),
    getCompanyProducts: builder.query({
      query: (payload) => ({
        url: `/application/product/list?applicationId=${payload.applicationId}`,
        method: 'GET',
        headers: {
          'Content-type': 'application/json; charset=UTF-8',
          'X-API-KEY': API_KEY,
          Authorization: `Bearer ${payload.token}`
        }
      })
    }),
    getCompanyCarriers: builder.query({
      query: (payload) => ({
        url: `/application/carrier/list?applicationId=${payload.applicationId}&productIds=${payload.productIds}`,
        method: 'GET',
        headers: {
          'Content-type': 'application/json; charset=UTF-8',
          'X-API-KEY': API_KEY,
          Authorization: `Bearer ${payload.token}`
        }
      })
    }),
    getAvailableAgencyProducts: builder.query({
      query: (payload) => ({
        url: `/agency-product-carrier/available-products?agencyId=${payload.agencyId}&limit=${payload.limit}&offset=${payload.offset}`,
        method: 'GET',
        headers: {
          'Content-type': 'application/json; charset=UTF-8',
          'X-API-KEY': API_KEY,
          Authorization: `Bearer ${payload.token}`
        }
      })
    }),
    getAgencyProductCarriers: builder.query({
      query: (payload) => ({
        url: `/agency-product-carrier/list?agencyId=${payload.agencyId}&limit=${payload.limit}&offset=${payload.offset}&productId=${payload.productId}`,
        method: 'GET',
        headers: {
          'Content-type': 'application/json; charset=UTF-8',
          'X-API-KEY': API_KEY,
          Authorization: `Bearer ${payload.token}`
        }
      })
    }),

    authrizedCompanyDetail: builder.query({
      keepUnusedDataFor: 1,
      query: (payload) => ({
        url: `/company-product/product-company-detail?productCompanyId=${payload.id}`,
        method: 'GET',
        headers: {
          'Content-type': 'application/json; charset=UTF-8',
          'X-API-KEY': API_KEY,
          Authorization: `Bearer ${payload.token}`
        }
      }),
      transformResponse: (response) => response.result
    }),

    addAuthrizedCompany: builder.mutation({
      query: (payload) => ({
        url: '/company-product/add-company-product',
        method: 'POST',
        body: payload.data,
        headers: {
          'Content-type': 'application/json; charset=UTF-8',
          'X-API-KEY': API_KEY,
          Authorization: `Bearer ${payload.token}`
        }
      }),
      invalidatesTags: ['Post']
    }),

    updateAuthrizedCompany: builder.mutation({
      query: (payload) => ({
        url: '/company-product/update-company-product',
        method: 'PUT',
        body: payload.data,
        headers: {
          'Content-type': 'application/json; charset=UTF-8',
          'X-API-KEY': API_KEY,
          Authorization: `Bearer ${payload.token}`
        }
      }),
      invalidatesTags: ['Put']
    }),

    updateAgencyProductMapping: builder.mutation({
      query: ({ productCompanyId, status, token }) => ({
        url: '/agency-product-carrier/update-product-mapping',
        method: 'PUT',
        body: { productCompanyId, status },
        headers: {
          'Content-type': 'application/json; charset=UTF-8',
          'X-API-KEY': API_KEY,
          Authorization: `Bearer ${token}`
        }
      })
    }),

    updateAgencyProductCarrierMapping: builder.mutation({
      query: ({ agencyProductCarrierMappingId, status, token }) => ({
        url: '/agency-product-carrier/update-product-carrier-mapping',
        method: 'PUT',
        body: { agencyProductCarrierMappingId, status },
        headers: {
          'Content-type': 'application/json; charset=UTF-8',
          'X-API-KEY': API_KEY,
          Authorization: `Bearer ${token}`
        }
      })
    }),

    // New endpoints for Add Product functionality
    getUnassignedProducts: builder.query({
      query: (payload) => ({
        url: `/agency-product-carrier/unassigned-products?agencyId=${payload.agencyId}&searchBy=${payload.searchBy || ''}`,
        method: 'GET',
        headers: {
          'Content-type': 'application/json; charset=UTF-8',
          'X-API-KEY': API_KEY,
          Authorization: `Bearer ${payload.token}`
        }
      })
    }),

    addProductToAgency: builder.mutation({
      query: ({ agencyId, productId, token }) => ({
        url: '/agency-product-carrier/add-product-to-agency',
        method: 'POST',
        body: { agencyId, productId },
        headers: {
          'Content-type': 'application/json; charset=UTF-8',
          'X-API-KEY': API_KEY,
          Authorization: `Bearer ${token}`
        }
      }),
      invalidatesTags: ['Post']
    })
  })
});

export const {
  useAddAuthrizedCompanyMutation,
  useGetAuthrizedCompanyDetailsQuery,
  useAuthrizedCompanyDetailQuery,
  useUpdateAuthrizedCompanyMutation,
  useLazyGetAgencyProductsQuery,
  useLazyGetCompanyProductsQuery,
  useLazyGetCompanyCarriersQuery,
  useGetAvailableAgencyProductsQuery,
  useLazyGetAgencyProductCarriersQuery,
  useUpdateAgencyProductMappingMutation,
  useUpdateAgencyProductCarrierMappingMutation,
  useGetUnassignedProductsQuery,
  useAddProductToAgencyMutation
} = companyProductApi;
